import React, { useState, useMemo, useRef } from "react";
import axios from "axios";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import { Box, CircularProgress, InputBase, List, ListItem, ListItemAvatar, ListItemText, Grid, Divider, IconButton, Button, Typography, ListItemSecondaryAction } from "@material-ui/core";
import Layout from "../components/Layout/Layout";
import { Refresh, Search, ArrowForwardIos, HighlightOffRounded, CheckCircleOutlineRounded, Clear } from "@material-ui/icons";

async function fetchEmployees() {
    const response = await axios.get(`/api/timeregistration/GetAllEmployees`);
    if (response.status !== 200) throw new Error(`fetchProjectLine: ${response.status}`);
    return response.data;
}

export default function EmployeeList() {
    const [filter, setFilter] = useState("");
    const history = useHistory();
    const searchInputRef = useRef(null);
    const [isSearchFocused, setIsSearchFocused] = useState(false);

    const { data, isLoading, isError, refetch, isRefetching } = useQuery("employees", fetchEmployees, {
        refetchInterval: 300000, // refetch every 5 minutes
        refetchOnWindowFocus: false,
    });

    const employees = useMemo(() => {
        return Array.isArray(data) && data.length > 0 ? data?.filter((e) => e.nationalRegistryId.startsWith(filter) || e.name.toLowerCase().includes(filter.toLowerCase())) : [];
    }, [data, filter]);

    const handleEmployeeClick = (employee) => {
        history.push(`/timeclock/${employee.id}/${employee.name}`);
    };

    if (isError) {
        return (
            <Layout title="Time Registration" variant="appbar" color="rgb(141,198,63)">
                <Box display="flex" flexDirection="column" alignItems="center" p={3}>
                    <Typography variant="h6" color="error">
                        Error loading time registrations. Please try again.
                    </Typography>
                    <Button variant="contained" color="primary" onClick={() => refetch()} style={{ marginTop: "20px" }}>
                        Retry
                    </Button>
                </Box>
            </Layout>
        );
    }

    return (
        <Layout
            title="Time Registration"
            variant="appbar"
            color="rgb(141,198,63)"
            searchBar={
                <>
                    <Box style={{ display: "flex", alignItems: "center", marginLeft: "auto", width: isSearchFocused || filter ? "60%" : "30%", transition: "width 0.3s" }}>
                        <Grid container alignItems="center">
                            <Grid item>
                                <IconButton
                                    onClick={() => {
                                        if (searchInputRef.current) {
                                            searchInputRef.current.focus();
                                            setIsSearchFocused(true);
                                        }
                                    }}
                                    disabled={isRefetching || isLoading}
                                >
                                    <Search style={{ color: "white" }} />
                                </IconButton>
                            </Grid>
                            <Grid item xs>
                                <InputBase
                                    inputRef={searchInputRef}
                                    fullWidth
                                    placeholder="Search..."
                                    value={filter}
                                    onFocus={() => setIsSearchFocused(true)}
                                    onBlur={() => setIsSearchFocused(false)}
                                    onChange={(e) => setFilter(e.target.value)}
                                    style={{
                                        color: "white",
                                        fontSize: "1.2rem",
                                        paddingLeft: "0.25rem",
                                        backgroundColor: "rgba(255, 255, 255, 0.15)",
                                        borderRadius: "4px",
                                    }}
                                    endAdornment={
                                        <IconButton onClick={() => setFilter("")} style={{ visibility: filter ? "visible" : "hidden", paddingTop: 0, paddingBottom: 0 }}>
                                            <Clear style={{ color: "white" }} />
                                        </IconButton>
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    <IconButton
                        onClick={refetch}
                        disabled={isRefetching || isLoading}
                        style={{
                            color: isRefetching || isLoading ? "lightgray" : "white",
                            opacity: isRefetching || isLoading ? 0.5 : 1,
                            marginLeft: "1rem",
                        }}
                    >
                        <Refresh />
                    </IconButton>
                </>
            }
        >
            {isRefetching || isLoading ? (
                <Box display="flex" justifyContent="center" mt={3}>
                    <CircularProgress size={50} />
                </Box>
            ) : employees.length > 0 ? (
                <List style={{ width: "100%", overflow: "auto", padding: "0 1rem" }}>
                    {employees?.map((employee) => (
                        <React.Fragment key={employee.id}>
                            <ListItem button onClick={() => handleEmployeeClick(employee)} style={{ padding: "20px" }}>
                                <ListItemAvatar>
                                    {employee.isCheckedIn ? (
                                        <CheckCircleOutlineRounded fontSize="large" style={{ color: "#28a745" }} />
                                    ) : (
                                        <HighlightOffRounded fontSize="large" style={{ color: "#dc3545" }} />
                                    )}
                                </ListItemAvatar>
                                <ListItemText
                                    primary={employee.name}
                                    primaryTypographyProps={{
                                        style: { fontSize: "1.25rem" },
                                    }}
                                    secondary={employee.nationalRegistryId != null ? employee.nationalRegistryId.slice(0, 6) : "-"}
                                    secondaryTypographyProps={{ style: { fontSize: "1rem" } }}
                                />
                                <ListItemSecondaryAction>
                                    <IconButton onClick={() => handleEmployeeClick(employee)}>
                                        <ArrowForwardIos />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                            <Divider component="li" variant="inset" />
                        </React.Fragment>
                    ))}
                </List>
            ) : filter ? (
                <Box display="flex" justifyContent="center" p={3}>
                    <Typography variant="subtitle1" style={{ fontSize: "1.2rem" }}>
                        No results found for "{filter}".
                    </Typography>
                </Box>
            ) : null}
        </Layout>
    );
}
